import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ServicePageTemplate({
  title = "",
  subtitle,
  description = "",
  image,
  features,
  benefits,
  process,
  icon: Icon,
  ctaText = "Get In Touch",
  subServices = [],
  accentColor = "pink"
}) {
  const colors = {
    pink: {
      accent: "#EC4899",
      bg: "bg-gradient-to-r from-purple-500 to-pink-500",
      bgLight: "bg-gradient-to-r from-purple-500/10 to-pink-500/10",
      text: "text-pink-400",
      hover: "hover:opacity-90",
      border: "border-pink-500/50",
      glow: "shadow-[0_0_30px_rgba(236,72,153,0.3)]"
    },
    blue: {
      accent: "#8B5CF6",
      bg: "bg-gradient-to-r from-blue-500 to-purple-500",
      bgLight: "bg-gradient-to-r from-blue-500/10 to-purple-500/10",
      text: "text-purple-400",
      hover: "hover:opacity-90",
      border: "border-purple-500/50",
      glow: "shadow-[0_0_30px_rgba(139,92,246,0.3)]"
    }
  };

  const color = colors[accentColor];

  return (
    <div className="pt-24 md:pt-32 bg-black min-h-screen">
      {/* Hero Section */}
      <section className="container-custom py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="animate-fade-in-up">
            {Icon && (
              <div className={`w-16 h-16 rounded-2xl ${color.bgLight} flex items-center justify-center mb-6`}>
                <Icon className={`w-8 h-8 ${color.text}`} />
              </div>
            )}

            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 text-white">
              {title}
            </h1>

            {subtitle && (
              <p className={`text-xl md:text-2xl ${color.text} font-medium mb-6`}>
                {subtitle}
              </p>
            )}

            <p className="text-white/60 text-lg leading-relaxed mb-8 max-w-xl">
              {description}
            </p>

            {/* Sub-services tags */}
            {subServices.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {subServices.map((service, idx) => (
                  <span 
                    key={idx}
                    className={`px-4 py-2 rounded-full ${color.bgLight} ${color.text} text-sm font-medium`}
                  >
                    {service}
                  </span>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-4">
              <Button
                asChild
                data-testid="service-cta-button"
                className={`${color.bg} ${color.hover} text-white rounded-full px-8 py-6 text-lg`}
              >
                <Link to="/contact">
                  {ctaText} <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>

          {/* Image */}
          <div className="animate-fade-in-up animation-delay-200">
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src={image}
                alt={title}
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      {features && features.length > 0 && (
        <section className="bg-[#0A0A0A] py-16 md:py-24">
          <div className="container-custom">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              What We Offer
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  data-testid={`feature-card-${index}`}
                  className="bg-[#111111] rounded-xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 hover:-translate-y-1"
                >
                  {feature.icon && (
                    <div className={`w-12 h-12 rounded-xl ${index % 2 === 0 ? "bg-gradient-to-r from-purple-500 to-pink-500/10" : "bg-gradient-to-r from-blue-500 to-purple-500/10"} flex items-center justify-center mb-4`}>
                      <feature.icon className={`w-6 h-6 ${index % 2 === 0 ? "text-pink-400" : "text-purple-400"}`} />
                    </div>
                  )}
                  <h3 className="font-heading text-lg font-semibold mb-2 text-white">
                    {feature.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits Section */}
      {benefits && benefits.length > 0 && (
        <section className="py-16 md:py-24 bg-black">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold mb-8 text-white">
                  Why Companies Choose Us...
                </h2>

                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <div
                      key={index}
                      data-testid={`benefit-item-${index}`}
                      className="flex items-start gap-4 p-4 rounded-xl hover:bg-white/5 transition-colors"
                    >
                      <div className={`w-6 h-6 rounded-full ${index % 2 === 0 ? "bg-gradient-to-r from-purple-500 to-pink-500" : "bg-gradient-to-r from-blue-500 to-purple-500"} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1 text-white">{benefit.title}</h4>
                        <p className="text-white/60 text-sm">{benefit.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl p-8 md:p-12 border border-white/10">
                <h3 className="font-heading text-2xl font-bold mb-4 text-white">
                  Ready to get started?
                </h3>
                <p className="text-white/60 mb-6">
                  Let's discuss how we can help your business grow with our {title.toLowerCase()} services.
                </p>
                <Button
                  asChild
                  data-testid="benefit-section-cta"
                  className={`${color.bg} ${color.hover} text-white rounded-full px-8 py-6`}
                >
                  <Link to="/contact">
                    Contact Us <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Process Section */}
      {process && process.length > 0 && (
        <section className="bg-[#0A0A0A] py-16 md:py-24">
          <div className="container-custom">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-4 text-white">
              Our Process
            </h2>
            <p className="text-white/60 text-center mb-12 max-w-2xl mx-auto">
              We follow a proven methodology to deliver exceptional results for every project.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {process.map((step, index) => (
                <div
                  key={index}
                  data-testid={`process-step-${index}`}
                  className="relative bg-[#111111] rounded-xl p-6 border border-white/10"
                >
                  <div className={`w-10 h-10 rounded-full ${index % 2 === 0 ? "bg-gradient-to-r from-purple-500 to-pink-500" : "bg-gradient-to-r from-blue-500 to-purple-500"} text-white font-heading font-bold flex items-center justify-center mb-4`}>
                    {index + 1}
                  </div>
                  <h3 className="font-heading text-lg font-semibold mb-2 text-white">
                    {step.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-black">
        <div className="container-custom">
          <div className="relative rounded-3xl p-8 md:p-16 text-center text-white overflow-hidden">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-transparent to-pink-500/20" />
            <div className="absolute inset-0 border border-white/10 rounded-3xl" />
            
            <div className="relative z-10">
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                Let's Build Something <span className="text-gradient">Great</span>
              </h2>
              <p className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">
                Ready to take your business to the next level? Get in touch today and let's discuss your project.
              </p>
              <Button
                asChild
                data-testid="final-cta-button"
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:bg-gradient-to-r from-purple-500 to-pink-500/90 text-white rounded-full px-10 py-6 text-lg"
              >
                <Link to="/contact">
                  Get In Touch <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
